import { useTheme } from '@emotion/react'
import React from 'react'
import {
    SponsoredTopic,
    SponsoredTopicProps,
} from '../../advertising/SponsoredTopic/SponsoredTopic'
import { SectionHeaderNavLink } from '../../collections'
import {
    StyledChevronRight,
    StyledHeading,
    StyledHeadingLink,
    StyledIconClock,
    StyledImageContainer,
    StyledLink,
    StyledList,
    StyledListItem,
    StyledMastheadContainer,
    StyledMastheadLink,
    StyledSectionHeader,
    StyledSectionHeaderWrapper,
    StyledSevenNewsChevron,
    StyledWebLink,
} from '../../section-header/SectionHeader/SectionHeader.styled'
import {
    StyledSimpleHeading,
    StyledSimpleSectionHeadingContainer,
} from '../../section-header/SectionHeader/Styled/SimpleHeading.styled'
import { FontScales } from '../../__styling/settings/fontScale'
import { Section } from '../../__styling/settings/sections'
import { ThemedRender } from '../../__styling/themed-render'
import { SectionHeaderLogo } from './SectionHeaderLogo/SectionHeaderLogo'

export type SectionOverride =
    | Section
    | 'sponsored'
    | 'inline-content'
    | 'inline-content-cng'

export interface SectionHeaderProps {
    hasMargin?: boolean
    heading: string
    headingUrl?: string
    masthead?: SectionHeaderNavLink
    navLinks?: SectionHeaderNavLink[]
    section?: SectionOverride
    onClick?: (event: React.MouseEvent<HTMLElement>) => void
    className?: string
    canGrow?: boolean
    fontScale?: FontScales
    showClock?: boolean
    showChevron?: boolean
    showLogo?: boolean
    hideShowMoreLabel?: boolean
    customSponsorMargin?: string
    hideSponsorOnMobile?: boolean
    hideShowMoreButtonOnMobile?: boolean
    invisibleHeaderBreakpoint?: 'sm' | 'md' // Hides the section header visually but retains the space
    idTagName?: string
}

export const SectionHeader: React.FC<SectionHeaderProps & SponsoredTopicProps> =
    ({
        hasMargin,
        className,
        canGrow,
        heading,
        headingUrl,
        masthead,
        sponsor,
        hideSponsorOnMobile,
        customSponsorMargin,
        navLinks,
        section: sectionOverride,
        fontScale,
        onClick,
        showClock,
        showLogo,
        invisibleHeaderBreakpoint,
        showChevron,
        idTagName
    }) => {
        const theme = useTheme()
        const Icon = showClock ? StyledIconClock : StyledSevenNewsChevron

        if (
            sectionOverride === 'sponsored' ||
            sectionOverride === 'inline-content' ||
            sectionOverride === 'inline-content-cng'
        ) {
            return (
                <StyledSimpleSectionHeadingContainer section={sectionOverride}>
                    <StyledSimpleHeading
                        fontScale={fontScale}
                        section={sectionOverride}
                    >
                        {heading}
                    </StyledSimpleHeading>
                </StyledSimpleSectionHeadingContainer>
            )
        }
        return (
            <StyledSectionHeaderWrapper
                id={idTagName}
                sectionOverride={sectionOverride}
                className={className}
                hasMargin={hasMargin}
                canGrow={canGrow}
                sponsor={sponsor}
                masthead={masthead}
                invisibleHeaderBreakpoint={invisibleHeaderBreakpoint}
            >
                <StyledSectionHeader sponsor={sponsor}>
                    <ThemedRender
                        sevennews={() =>
                            showLogo ? (
                                <SectionHeaderLogo
                                    section={sectionOverride || theme.section}
                                    sectionUrl={headingUrl}
                                />
                            ) : (
                                <Icon />
                            )
                        }
                    />
                    {headingUrl ? (
                        <StyledHeadingLink
                            to={headingUrl}
                            openExternalInNewWindow={true}
                            onClick={onClick}
                            rel="noopener"
                        >
                            <StyledHeading
                                isLink={true}
                                fontScale={fontScale}
                                showLogo={showLogo}
                            >
                                {heading}
                                {showChevron && <StyledChevronRight />}
                            </StyledHeading>
                        </StyledHeadingLink>
                    ) : (
                        <StyledHeading
                            isLink={false}
                            fontScale={fontScale}
                            showLogo={showLogo}
                        >
                            {heading}
                            {showChevron && <StyledChevronRight />}
                        </StyledHeading>
                    )}
                    {navLinks && (
                        <StyledList>
                            {navLinks.map((item, index) => (
                                <StyledListItem
                                    section={theme.section}
                                    key={index}
                                >
                                    {item.isExternalLink ? (
                                        <StyledWebLink
                                            to={item.link}
                                            onClick={onClick}
                                        >
                                            {item.text}
                                        </StyledWebLink>
                                    ) : (
                                        <StyledLink
                                            to={item.link}
                                            onClick={onClick}
                                        >
                                            {item.text}
                                        </StyledLink>
                                    )}
                                </StyledListItem>
                            ))}
                        </StyledList>
                    )}

                    {sponsor && (
                        <StyledImageContainer
                            customMargin={customSponsorMargin}
                            hideOnMobile={hideSponsorOnMobile}
                        >
                            <SponsoredTopic sponsor={sponsor} />
                        </StyledImageContainer>
                    )}
                </StyledSectionHeader>

                {masthead && (
                    <StyledMastheadContainer>
                        <StyledMastheadLink
                            to={masthead.link}
                            onClick={onClick}
                        >
                            {masthead.text}
                        </StyledMastheadLink>
                    </StyledMastheadContainer>
                )}
            </StyledSectionHeaderWrapper>
        )
    }
SectionHeader.displayName = 'SectionHeader'
